/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import "typedb-web-common/lib/prism";

import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { RootComponent } from "./root.component";
import { importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from "@angular/material/core";
import { FirebaseOptions, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { provideNgxStripe } from "ngx-stripe";
import { provideHttpClient } from "@angular/common/http";
import { CloudRoutingModule, routes } from "./routing/routes";
import { TypeDBCloudTitleStrategy } from "./routing/title-strategy";
import { CloudAuthService } from "./service/authentication/cloud-auth.service";
import { lastValueFrom } from "rxjs";
import { MAT_DIALOG_DEFAULT_OPTIONS, MAT_DIALOG_SCROLL_STRATEGY } from "@angular/material/dialog";
import { provideRouter, TitleStrategy } from "@angular/router";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from "@angular/material/tooltip";
import { FormService } from "./service/form.service";
import { SanityService } from "./service/sanity.service";
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxDefaultOptions } from "@angular/material/checkbox";
import "posthog-js/dist/recorder";
import "posthog-js/dist/surveys";
import "posthog-js/dist/exception-autocapture";
import "posthog-js/dist/tracing-headers";
import "posthog-js/dist/web-vitals";
import "posthog-js/dist/dead-clicks-autocapture";
import posthog from "posthog-js/dist/module.no-external";
import Intercom from "@intercom/messenger-js-sdk";
import { Overlay } from "@angular/cdk/overlay";
import { NotificationService } from "./service/notification.service";
import { GaugeModule } from 'angular-gauge';

const rippleGlobalConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 0,
        exitDuration: 0,
    },
};

const tooltipGlobalConfig: MatTooltipDefaultOptions = {
    showDelay: 0,
    hideDelay: 0,
    touchendHideDelay: 0,
    position: "above",
};

const checkboxGlobalConfig: MatCheckboxDefaultOptions = {
    color: "primary"
};

let firebaseOptions: FirebaseOptions & { tenantId: string } = { tenantId: "" };

function startUserAuthInitializer(service: CloudAuthService, forms: FormService, _: SanityService, notifications: NotificationService) {
    return () => lastValueFrom(service.startUserAuth()).then((res) => {
        firebaseOptions = { tenantId: res.tenantId, apiKey: res.apiKey, authDomain: res.authDomain };
        forms.authToken = res.cioTrackApiToken;
        notifications.appId = res.novuAppId;
        posthog.init(
            res.posthogApiKey,
            {
                api_host: "https://typedb.com/ph",
                ui_host: "https://us.posthog.com",
                person_profiles: "always",
                capture_pageview: false,
                capture_pageleave: true,
            }
        );
    }).catch((err) => {
        console.error(`Error loading user auth configuration: ${err}`);
    });
}

Intercom({
    app_id: "zof896ic",
    hide_default_launcher: true,
    custom_launcher_selector: ".sidebar-support",
});

bootstrapApplication(RootComponent, {
    providers: [
        provideRouter(routes),
        { provide: TitleStrategy, useClass: TypeDBCloudTitleStrategy },
        provideHttpClient(),
        provideAnimations(),
        provideNgxStripe(),
        provideFirebaseApp(() => initializeApp(firebaseOptions)),
        provideAuth(() => {
            const auth = getAuth();
            auth.tenantId = firebaseOptions.tenantId;
            return auth;
        }),
        CloudRoutingModule,
        importProvidersFrom(GaugeModule.forRoot()),
        provideAppInitializer(() => {
          const initializerFn = (startUserAuthInitializer)(inject(CloudAuthService), inject(FormService), inject(SanityService), inject(NotificationService));
          return initializerFn();
        }),
        {
            provide: MAT_DIALOG_SCROLL_STRATEGY,
            useFactory: () => {
                const overlay = inject(Overlay)
                return () => overlay.scrollStrategies.noop();
            }
        },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { width: "480px" } },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: rippleGlobalConfig },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipGlobalConfig },
        { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: checkboxGlobalConfig },
    ]
});
